import {Avatar, AvatarRootProps, Box, Button, HStack, Menu, Text} from '@chakra-ui/react'
import {Tolgee} from '@cypro/ui/integrations/tolgee'
import {Link} from '@tanstack/react-router'
import {useTolgee, useTranslate} from '@tolgee/react'
import {MouseEvent} from 'react'
import {LuLogOut} from 'react-icons/lu'

export interface AccountMenuProps {
  user: {name: string; email: string}
  onLogout: () => void
  canLogout: boolean
  avatarProps?: AvatarRootProps
  displayName?: boolean
  href?: string
  onClickUserName?: (e: MouseEvent) => void
}

export function AccountMenu({
  user,
  onLogout,
  canLogout,
  avatarProps,
  displayName,
  href,
  onClickUserName,
}: AccountMenuProps) {
  const {t} = useTranslate('common')
  const {t: tLocales} = useTranslate('locales')
  const {changeLanguage: _changeLanguage, getLanguage} = useTolgee()

  const changeLanguage = async (value: string) => {
    Tolgee.persistLanguage(value)
    await _changeLanguage(value)
  }

  const userNameText = (
    <Text fontSize="sm" truncate maxW="4rem" title={user.name}>
      {user.name}
    </Text>
  )

  return (
    <Menu.Root positioning={{placement: 'right-end'}} closeOnSelect={false}>
      <Box display="flex" spaceX="3">
        <Menu.Trigger>
          <Avatar.Root size="xs" {...avatarProps} data-e2e="current-user" title={user.name}>
            <Avatar.Fallback name={user.name || ''} />
          </Avatar.Root>
        </Menu.Trigger>

        {displayName && (
          <Box>
            <Text fontSize="sm" fontWeight="medium">
              {user.name}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {user.email.toLowerCase()}
            </Text>
          </Box>
        )}
      </Box>

      <Menu.Positioner>
        <Menu.Content>
          <Menu.RadioItemGroup value={getLanguage()} onValueChange={event => changeLanguage(event.value)}>
            <Menu.ItemGroupLabel>{t('languages')}</Menu.ItemGroupLabel>
            {Tolgee.availableLanguages.map(lang => (
              <Menu.RadioItem key={lang} value={lang}>
                {tLocales(lang)}
                <Menu.ItemIndicator />
              </Menu.RadioItem>
            ))}
          </Menu.RadioItemGroup>

          <Menu.Separator />

          <HStack width="100%" justifyContent="space-between">
            <Button asChild unstyled onClick={onClickUserName}>
              {!href && userNameText}
              {href && <Link to={href}>{userNameText}</Link>}
            </Button>

            <Menu.Item
              value="logout"
              onClick={onLogout}
              disabled={!canLogout}
              color="fg.error"
              _hover={{bg: 'bg.error', color: 'fg.error'}}
              title={t('logout')}
              width="max-content"
            >
              <Box width="1rem" display="flex" justifyContent="center">
                <LuLogOut />
              </Box>
            </Menu.Item>
          </HStack>
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  )
}
